import diagnostics from "@/services/diagnostics";
/** One shared, gesture-unlocked context. Each screen owns and cancels its voices. */
const CLIPS = Object.freeze({
  click: { file: "01_button_click.wav", volume: 0.32, channel: "click" },
  search: { file: "02_matchmaking_search.wav", volume: 0.22, loop: true, channel: "music" },
  found: { file: "03_opponent_found.wav", volume: 0.65, channel: "cue" },
  start: { file: "05_match_start_gong.wav", volume: 0.6, channel: "cue" },
  signal: { file: "06_gotcha.wav", volume: 0.7, channel: "cue" },
  suspense: { file: "07_reveal_suspense.wav", volume: 0.32, loop: true, channel: "music" },
  reveal: { file: "08_loser_reveal.wav", volume: 0.65, channel: "cue" },
  tick: { file: "09_timer_tick.wav", volume: 0.16, channel: "tick" },
});
const LOOK_AHEAD_MS = 120;
let context = null;
let pageActive = true;
let appEnabled = () => false;
let interfaceSounds = null;
let abortLoads = null;
const encoded = new Map();
const decoded = new Map();
const scopes = new Set();

diagnostics.registerSource("audio", () => {
  const states = [...scopes].map(scope => scope.diagnosticState());
  return { context: context?.state, scopes: scopes.size,
    voices: states.reduce((sum, state) => sum + state.voices, 0),
    timers: states.reduce((sum, state) => sum + state.timers, 0) };
});

function visible() { return pageActive && document.visibilityState !== "hidden"; }

function fetchClip(kind) {
  if (!encoded.has(kind)) {
    const base = process.env.BASE_URL || "/";
    encoded.set(kind, fetch(base + "audio/" + CLIPS[kind].file, { signal: abortLoads?.signal })
      .then((response) => response.ok ? response.arrayBuffer() : null).catch(() => null));
  }
  return encoded.get(kind);
}

function bufferFor(kind) {
  if (!context) return Promise.resolve(null);
  if (!decoded.has(kind)) {
    const owner = context;
    decoded.set(kind, fetchClip(kind).then((bytes) => {
      if (!bytes || owner !== context || owner.state === "closed") return null;
      // Callback form also supports older iOS WebViews. No game operation awaits this promise.
      return new Promise((resolve) => {
        try {
          const decoding = owner.decodeAudioData(bytes.slice(0), resolve, () => resolve(null));
          // Some engines return a rejecting promise as well as calling the error callback.
          if (decoding?.catch) decoding.catch(() => resolve(null));
        }
        catch (error) { resolve(null); }
      });
    }).catch(() => null));
  }
  return decoded.get(kind);
}

export function refreshRoundAudio() {
  scopes.forEach((scope) => scope.refresh());
}

export function playInterfaceSound(kind) { interfaceSounds?.play(kind); }
export function stopInterfaceSound(kind) { interfaceSounds?.stop(kind); }

/** Installed once by App.vue. Includes native buttons, AppButton, links and keyboard activation. */
export function prepareRoundAudio(enabled) {
  pageActive = true;
  appEnabled = enabled;
  abortLoads = new AbortController();
  Object.keys(CLIPS).forEach(fetchClip);
  interfaceSounds = createRoundSounds(enabled);
  let disposed = false;
  const unlock = () => {
    if (disposed || !enabled() || !visible()) return;
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      if (!context) {
        context = new AudioContext({ latencyHint: "interactive" });
        context.onstatechange = refreshRoundAudio;
        Object.keys(CLIPS).forEach(bufferFor);
      }
      // resume() is invoked synchronously inside the gesture, including iOS's interrupted state.
      if (context.state !== "running") return context.resume().then(refreshRoundAudio).catch(() => {});
      refreshRoundAudio();
      return Promise.resolve();
    } catch (error) {
      // Unsupported/blocked audio must never affect navigation, camera or the round.
    }
  };
  const gesture = (event) => {
    if (!event.isTrusted) return;
    if (event.type === "keydown" && !["Enter", " "].includes(event.key)) return;
    unlock();
  };
  const click = (event) => {
    if (!event.isTrusted) return;
    const button = event.target?.closest?.("button, a[href], [role='button'], input[type='submit'], input[type='button']");
    if (!button || button.disabled || button.getAttribute("aria-disabled") === "true" ||
      button.getAttribute("aria-busy") === "true" || button.closest("[inert]")) return;
    const pressedAt = Date.now();
    const unlocking = unlock();
    if (context?.state === "running") playInterfaceSound("click");
    else unlocking?.then(() => {
      // On iOS resume may complete after click dispatch. Only the fresh user click may wait.
      if (!disposed && Date.now() - pressedAt <= 350) playInterfaceSound("click");
    });
  };
  const visibility = () => {
    refreshRoundAudio();
    if (visible() && context) unlock();
  };
  // Keep only the current loop/tick intent for a bfcache return, never live voices or timers.
  const pageHide = () => { pageActive = false; refreshRoundAudio(); };
  const pageShow = () => { pageActive = true; visibility(); };
  ["pointerdown", "touchend", "keydown"].forEach((name) => document.addEventListener(name, gesture, true));
  document.addEventListener("click", click, true);
  document.addEventListener("visibilitychange", visibility);
  window.addEventListener("pagehide", pageHide);
  window.addEventListener("pageshow", pageShow);
  return () => {
    disposed = true;
    ["pointerdown", "touchend", "keydown"].forEach((name) => document.removeEventListener(name, gesture, true));
    document.removeEventListener("click", click, true);
    document.removeEventListener("visibilitychange", visibility);
    window.removeEventListener("pagehide", pageHide);
    window.removeEventListener("pageshow", pageShow);
    [...scopes].forEach((scope) => scope.dispose());
    abortLoads.abort();
    if (context) {
      context.onstatechange = null;
      context.close().catch(() => {});
    }
    context = null;
    interfaceSounds = null;
    encoded.clear();
    decoded.clear();
  };
}

export function createRoundSounds(enabled) {
  const voices = new Map();
  let disposed = false;
  let loopKind = null;
  let tickOrigin = null;
  let tickTimer = null;
  let lastTick = 0;
  const allowed = () => !disposed && enabled() && appEnabled() && visible() && context?.state === "running";

  function cancelVoice(channel) {
    const voice = voices.get(channel);
    if (!voice) return;
    voices.delete(channel); // invalidates any pending fetch/decode callback too
    if (voice.source) {
      voice.source.onended = null;
      try { voice.source.stop(); } catch (error) { /* Already ended. */ }
      voice.source.disconnect();
      voice.gain.disconnect();
    }
  }

  function startVoice(kind, at = Date.now()) {
    if (!allowed()) return;
    const clip = CLIPS[kind];
    if (voices.get(clip.channel)?.kind === kind && clip.loop) return;
    cancelVoice(clip.channel);
    const voice = { kind, source: null, gain: null };
    voices.set(clip.channel, voice);
    const owner = context;
    bufferFor(kind).then((buffer) => {
      if (!buffer || !allowed() || owner !== context || voices.get(clip.channel) !== voice) {
        if (voices.get(clip.channel) === voice) voices.delete(clip.channel);
        return;
      }
      // Never replay an old event after loading, returning from background or unlocking audio.
      if (!clip.loop && Date.now() - at > (kind === "tick" ? 80 : 350)) {
        voices.delete(clip.channel);
        return;
      }
      try {
        const source = owner.createBufferSource();
        const gain = owner.createGain();
        voice.source = source;
        voice.gain = gain;
        source.buffer = buffer;
        source.loop = !!clip.loop;
        gain.gain.value = clip.volume;
        source.connect(gain);
        gain.connect(owner.destination);
        source.onended = () => {
          source.disconnect();
          gain.disconnect();
          if (voices.get(clip.channel) === voice) voices.delete(clip.channel);
        };
        source.start(owner.currentTime + Math.max(0, at - Date.now()) / 1000);
      } catch (error) {
        cancelVoice(clip.channel);
      }
    });
  }

  function clearTickTimer() {
    clearTimeout(tickTimer);
    tickTimer = null;
  }

  function pumpTicks() {
    clearTickTimer();
    if (tickOrigin === null || !allowed()) return;
    const now = Date.now();
    // Skip missed seconds; never emit a burst after a stalled frame or background suspension.
    const second = Math.max(lastTick + 1, 1, Math.ceil((now - tickOrigin) / 1000));
    const at = tickOrigin + second * 1000;
    if (at - now <= LOOK_AHEAD_MS) {
      lastTick = second;
      startVoice("tick", at);
    }
    tickTimer = setTimeout(pumpTicks, Math.min(50, Math.max(10, at - now - LOOK_AHEAD_MS)));
  }

  const scope = {
    diagnosticState() { return { voices: voices.size, timers: Number(tickTimer !== null) }; },
    play(kind) {
      if (disposed || !CLIPS[kind]) return;
      if (CLIPS[kind].loop) loopKind = kind;
      startVoice(kind);
    },
    stop(kind) {
      if (!kind) {
        loopKind = null;
        scope.stopTicks();
        [...voices.keys()].forEach(cancelVoice);
      } else {
        if (kind === loopKind) loopKind = null;
        if (voices.get(CLIPS[kind]?.channel)?.kind === kind) cancelVoice(CLIPS[kind].channel);
      }
    },
    startTicks(startedAt) {
      if (disposed || !Number.isFinite(startedAt)) return;
      if (tickOrigin !== startedAt) {
        scope.stopTicks();
        tickOrigin = startedAt;
        lastTick = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
      }
      pumpTicks();
    },
    stopTicks() {
      tickOrigin = null;
      lastTick = 0;
      clearTickTimer();
      cancelVoice("tick");
    },
    refresh() {
      if (!allowed()) {
        clearTickTimer();
        [...voices.keys()].forEach(cancelVoice);
        return;
      }
      if (loopKind) startVoice(loopKind);
      if (tickOrigin !== null && tickTimer === null) pumpTicks();
    },
    dispose() {
      scope.stop();
      disposed = true;
      scopes.delete(scope);
    },
  };
  scopes.add(scope);
  return scope;
}
